import {
  documentApi,
  lessonSelectors,
  reduxTypedHooks
} from 'common/modules/ReactShared';
import { useCallback, useEffect, useState } from 'react';
import {
  IDocument,
  tDocumentRating,
  tDocumentUpdateRequest
} from 'common/modules/ReactShared/data/api/document/types';
import { assignmentDocumentAdapter } from './adapters';
import { tAssignmentNormalizedData, tAudioSubmitType, tRequestStatus } from './types';

type tArgs = {
  taskId: number | null;
  lessonId: number | null;
  documentId: number | null;
};
type tData = tAssignmentNormalizedData | null;
type tReturnType = {
  documentResource: {
    status: tRequestStatus;
    data: tData;
    pendingText: string | null;
  };
  actions: {
    fetchAssignment: () => Promise<tAssignmentNormalizedData | null>;
    submitTextAnswer: (text: string) => Promise<void>;
    submitAudioAnswer: (
      audio: tAudioSubmitType
    ) => Promise<tAssignmentNormalizedData | null>;
    submitRate: (rating: tDocumentRating) => Promise<tAssignmentNormalizedData | null>;
  };
};

const initialResource = {
  status: 'initial' as const,
  data: null,
  pendingText: null
};
const useAssignmentActions = ({ taskId, lessonId, documentId }: tArgs): tReturnType => {
  const [documentResource, setDocumentResource] = useState<{
    status: tRequestStatus;
    data: tData;
    pendingText: string | null;
  }>(initialResource);

  useEffect(() => {
    setDocumentResource(initialResource);
  }, [taskId]);

  const _documentId = documentId || documentResource.data?.assignmentId;
  // An author playing the lesson from Teach hands in to the preview endpoints (see `lessonPreview`).
  const previewLessonId = reduxTypedHooks.useTypedSelector(
    lessonSelectors.selectPreviewLessonId
  );
  const preview = !!lessonId && previewLessonId === lessonId;

  const fetchAssignment = useCallback(async () => {
    setDocumentResource({ status: 'fetch_loading', data: null, pendingText: null });
    let data: IDocument | null = null;

    if (_documentId) {
      data = await fetchDocumentById(_documentId);
    } else if (lessonId && taskId) {
      data = await fetchDocumentForTask(lessonId, taskId, preview);
    }

    const normalizedData = data
      ? assignmentDocumentAdapter({ assignmentDoc: data })
      : null;

    setDocumentResource({ status: 'received', data: normalizedData, pendingText: null });

    return normalizedData;
  }, [taskId, lessonId, _documentId, preview]);

  const submitTextAnswer = useCallback(
    async (content: string) => {
      let data: IDocument | null = null;

      setDocumentResource((prevResource) => ({
        status: 'submit_loading',
        data: prevResource.data,
        pendingText: content
      }));

      try {
        if (_documentId) {
          if (!isAnswerSameAsBefore(content, documentResource.data)) {
            data = await updateDocument({ documentId: _documentId, data: { content } });
          } else {
            return;
          }
        } else if (lessonId && taskId) {
          data = await createTaskDocument({
            lessonId,
            taskId,
            preview,
            data: { content }
          });
        }

        const normalizedData = data
          ? assignmentDocumentAdapter({ assignmentDoc: data })
          : null;

        setDocumentResource({
          status: 'received',
          data: normalizedData,
          pendingText: null
        });
      } catch (error) {
        // document creation failed
        setDocumentResource((prevResource) => ({
          status: 'submit_failed',
          data: prevResource.data,
          pendingText: null
        }));

        throw new Error('Failed text answer submit');
      }
    },
    [documentResource.data, _documentId, lessonId, taskId, preview]
  );

  const submitAudioAnswer = useCallback(
    async (audio: tAudioSubmitType) => {
      let data: IDocument | null = null;
      const payload = audio instanceof FormData ? audio : { audio };
      setDocumentResource((prevResource) => ({
        status: 'submit_loading',
        data: prevResource.data,
        pendingText: null
      }));
      try {
        if (_documentId) {
          data = await updateDocument({ documentId: _documentId, data: payload });
        } else if (lessonId && taskId) {
          data = await createTaskDocument({ lessonId, taskId, preview, data: payload });
        }

        const normalizedData = data
          ? assignmentDocumentAdapter({ assignmentDoc: data })
          : null;

        setDocumentResource({
          status: 'received',
          data: normalizedData,
          pendingText: null
        });

        return normalizedData;
      } catch (error) {
        // document creation failed
        setDocumentResource((prevResource) => ({
          status: 'submit_failed',
          data: prevResource.data,
          pendingText: null
        }));

        throw new Error('Failed audio answer submit');
      }
    },
    [taskId, lessonId, _documentId, preview]
  );

  const submitRate = useCallback(
    async (rating: tDocumentRating) => {
      if (!_documentId) return null;

      setDocumentResource((prevResource) => ({
        status: 'rate_loading',
        data: prevResource.data,
        pendingText: null
      }));

      const data = await submitRating({ documentId: _documentId, rating });

      const normalizedData = data
        ? assignmentDocumentAdapter({ assignmentDoc: data })
        : null;

      setDocumentResource({
        status: 'received',
        data: normalizedData,
        pendingText: null
      });

      return normalizedData;
    },
    [_documentId]
  );

  return {
    documentResource,
    actions: {
      fetchAssignment,
      submitTextAnswer,
      submitAudioAnswer,
      submitRate
    }
  };
};

export default useAssignmentActions;

async function fetchDocumentForTask(lessonId: number, taskId: number, preview: boolean) {
  try {
    const { document } = await documentApi.fetchTaskDocument(lessonId, taskId, preview);

    return document;
  } catch (_) {
    return null;
  }
}

async function fetchDocumentById(documentId: number) {
  try {
    const { document } = await documentApi.fetchDocument(documentId);

    return document;
  } catch (_) {
    return null;
  }
}

type tDocumentUpdateData = tDocumentUpdateRequest | FormData;

async function createTaskDocument({
  lessonId,
  taskId,
  preview,
  data
}: {
  lessonId: number;
  taskId: number;
  preview: boolean;
  data: tDocumentUpdateData;
}) {
  const { document } = await documentApi.createDocument(lessonId, taskId, data, preview);
  return document;
}

async function updateDocument({
  documentId,
  data
}: {
  documentId: number;
  data: tDocumentUpdateData;
}) {
  try {
    const { document } = await documentApi.updateDocument(documentId, data);

    return document;
  } catch (error) {
    return null;
  }
}

async function submitRating({
  documentId,
  rating
}: {
  documentId: number;
  rating: tDocumentRating;
}) {
  try {
    const { document } = await documentApi.submitDocumentRating(documentId, rating);

    return document;
  } catch (error) {
    return null;
  }
}

function isAnswerSameAsBefore(
  newAnswer: string,
  assignmentNormalizedData: tAssignmentNormalizedData | null
): boolean {
  if (!assignmentNormalizedData) return false;

  const lastStudentMessage = assignmentNormalizedData.conversation
    .reverse()
    .find((message) => message.type === 'Document');

  return lastStudentMessage?.content === newAnswer;
}
